import { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { SESSION_TYPES } from '@medflex/utils/constants';
import { Sources } from '@medflex/shared-types';
import useUserProfile from './useUserProfile';

const refineSessionsFunction = userProfileId => session => ({
  ...session,
  deactivated: session.participants
    .filter(participant => participant.userId !== userProfileId)
    .every(participant => !!participant.deactivated),
  ...(session.reference?.data?.source === Sources.PRACTICE &&
    !session.messageLastReadId && {
      messagesUnreadCount: 1,
    }),
});

const useSessionChats = () => {
  const { userProfile } = useUserProfile();
  const { sessions } = useSelector(state => state.sessions);
  const dispatch = useDispatch();
  const isLoading = useSelector(state => state.loading.effects.sessions.load);
  const { load: loadingStatus } = useSelector(state => state.sessions.status);

  const chats = useMemo(() => {
    if (!userProfile) return [];
    return sessions
      .filter(({ type }) =>
        [SESSION_TYPES.CHAT, SESSION_TYPES.INTERNAL1ON1, SESSION_TYPES.TEAM_CHAT].includes(type),
      )
      .map(refineSessionsFunction(userProfile.id));
  }, [sessions, userProfile]);

  const allSessionChats = useMemo(() => {
    if (!userProfile) return [];
    return sessions
      .filter(({ type }) =>
        [
          SESSION_TYPES.CHAT,
          SESSION_TYPES.INTERNAL1ON1,
          SESSION_TYPES.TEAM_CHAT,
          SESSION_TYPES.MEETING,
        ].includes(type),
      )
      .map(refineSessionsFunction(userProfile.id));
  }, [sessions, userProfile]);

  const pamSessionChats = useMemo(() => {
    if (!userProfile) return [];
    return sessions
      .filter(({ type, reference }) => [SESSION_TYPES.PAM_CHAT].includes(type) && reference)
      .map(refineSessionsFunction(userProfile.id));
  }, [sessions, userProfile]);

  const create = ({ title, ...data }) => {
    const attributes = {
      type: 'chat',
      ...data,
      ...(title && title.trim().length > 0 && { title }),
    };
    return dispatch.sessions.createChat(attributes);
  };

  const sendMulticast = payload => dispatch.sessionChats.sendMulticast(payload);

  const load = () => dispatch.sessions.load();

  return {
    create,
    load,
    chats,
    isLoading,
    loadingStatus,
    sendMulticast,
    allSessionChats,
    pamSessionChats,
  };
};

export default useSessionChats;
